// src/pages/CreateInvoice.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  getAllCustomers, addInvoice, getAllInvoiceItems, 
  getAllAccounts, getNextInvoiceNumber, getAllStudents,
  addCustomer, addInvoiceItem, getFilteredLogs,
  subscribeCategories, DEFAULT_CATEGORIES
} from "../firebase/database";
import { theme } from "../utils/theme";
import { HHMMToMinutes, minutesToHHMM, formatDate, todayStr } from "../utils/helpers";

const C = theme.colors;

export default function CreateInvoice() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [customerFilter, setCustomerFilter] = useState("business"); // "business" | "student" | "all"
  
  // Log selection states
  const [allRecentLogs, setAllRecentLogs] = useState([]);
  const [logFilterStaffId, setLogFilterStaffId] = useState("");
  const [logFilterDate, setLogFilterDate] = useState(todayStr());
  const [logFilterRole, setLogFilterRole] = useState("staff"); // "staff" or "all"
  const [logFilterCategoryId, setLogFilterCategoryId] = useState("");
  const [selectedLogIds, setSelectedLogIds] = useState([]);

  // Modal States
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  
  // Quick Add Form States
  const [newCustomerData, setNewCustomerData] = useState({ name: "", phone: "", address: "" });
  const [newItemData, setNewItemData] = useState({ name: "", rate: "", description: "", category: "Service" });

  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    staffId: "",
    staffName: "",
    date: new Date().toISOString().split("T")[0],
    status: "pending",
    paymentMethod: "",
    items: [],
    linkedLogs: [], // Array of log objects
  });

  const [newItem, setNewItem] = useState({
    itemId: "",
    itemName: "",
    description: "",
    quantity: 1,
    rate: 0,
    total: 0,
  });

  const [fetchingLogs, setFetchingLogs] = useState(false);

  useEffect(() => {
    loadData();
    const unsubCats = subscribeCategories(setCategories);
    return () => {
      if (unsubCats) unsubCats();
    };
  }, []);

  const handleFetchLogs = async () => {
    setFetchingLogs(true);
    try {
      // Pull only targeted data based on selected filters (Highly cost-efficient)
      const logs = await getFilteredLogs({
        studentId: logFilterStaffId,
        date: logFilterDate,
      });
      setAllRecentLogs(logs);
    } catch (err) {
      console.error("Error fetching logs:", err);
      alert(`Failed to pull logs. Error: ${err.message || "Unknown error"}. Please check your internet or browser console.`);
    } finally {
      setFetchingLogs(false);
    }
  };

  const loadData = async () => {
    try {
      const [customersData, itemsData, accountsData, nextNo, studentsData] = await Promise.all([
        getAllCustomers(),
        getAllInvoiceItems(),
        getAllAccounts(),
        getNextInvoiceNumber(),
        getAllStudents(),
      ]);
      setCustomers(customersData);
      setItems(itemsData);
      setAccounts(accountsData.filter(a => a.nature === "Cash" || a.nature === "Bank"));
      setInvoiceNo(nextNo);
      setAllPeople(studentsData);
      // Filter for staff members (role === "staff" or "trainer" or "instructor")
      const staff = studentsData.filter(s => ["staff", "trainer", "instructor", "admin"].includes(s.role));
      setStaffList(staff);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    // Look in both lists to find the name
    const person = [...customers, ...allPeople].find(p => p.id === id);
    setFormData(prev => ({
      ...prev,
      customerId: id,
      customerName: person?.name || "",
    }));
  };

  const handleStaffChange = (e) => {
    const staffId = e.target.value;
    const staff = staffList.find(s => s.id === staffId);
    setFormData(prev => ({
      ...prev,
      staffId,
      staffName: staff?.name || "",
    }));
  };

  const handleItemChange = (e) => {
    const itemId = e.target.value;
    const item = items.find(i => i.id === itemId);
    if (item) {
      setNewItem(prev => ({
        ...prev,
        itemId,
        itemName: item.name,
        description: item.description || "",
        rate: item.rate || 0,
        total: (prev.quantity || 1) * (item.rate || 0),
      }));
    }
  };

  const handleQuantityChange = (qty) => {
    const quantity = parseFloat(qty) || 0;
    setNewItem(prev => ({
      ...prev,
      quantity,
      total: quantity * prev.rate,
    }));
  };

  const handleRateChange = (rate) => {
    const newRate = parseFloat(rate) || 0;
    setNewItem(prev => ({
      ...prev,
      rate: newRate,
      total: prev.quantity * newRate,
    }));
  };

  const handleAddItem = () => {
    if (!newItem.itemId || newItem.rate <= 0) {
      alert("Please select an item");
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem }],
    }));
    setNewItem({ itemId: "", itemName: "", description: "", quantity: 1, rate: 0, total: 0 });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleEditItem = (index) => {
    const item = formData.items[index];
    setNewItem({ ...item });
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const totalMinutes = useMemo(() => {
    return formData.linkedLogs.reduce((sum, log) => sum + (HHMMToMinutes(log.endTime) - HHMMToMinutes(log.startTime)), 0);
  }, [formData.linkedLogs]);

  const valuePerHour = useMemo(() => {
    const total = calculateTotal();
    const hours = totalMinutes / 60;
    return hours > 0 ? total / hours : 0;
  }, [formData.items, totalMinutes]);

  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      status,
      paymentMethod: status === "paid" ? prev.paymentMethod : "",
    }));
  };

  const handleQuickAddCustomer = async () => {
    if (!newCustomerData.name) return;
    try {
      const id = await addCustomer(newCustomerData);
      const updated = await getAllCustomers();
      setCustomers(updated);
      setFormData(prev => ({ ...prev, customerId: id, customerName: newCustomerData.name }));
      setShowCustomerModal(false);
      setNewCustomerData({ name: "", phone: "", address: "" });
    } catch (err) {
      alert("Failed to add customer");
    }
  };

  const handleQuickAddItem = async () => {
    if (!newItemData.name || !newItemData.rate) return;
    try {
      const id = await addInvoiceItem({ ...newItemData, rate: parseFloat(newItemData.rate) });
      const updated = await getAllInvoiceItems();
      setItems(updated);
      setNewItem(prev => ({
        ...prev,
        itemId: id,
        itemName: newItemData.name,
        description: newItemData.description || "",
        rate: parseFloat(newItemData.rate),
        total: prev.quantity * parseFloat(newItemData.rate)
      }));
      setShowItemModal(false);
      setNewItemData({ name: "", rate: "", description: "", category: "Service" });
    } catch (err) {
      alert("Failed to add item");
    }
  };

  // Enrich logs with names and handle empty state
  const logsWithNames = useMemo(() => {
    return allRecentLogs.map(log => {
      const person = allPeople.find(s => s.id === log.studentId);
      return {
        ...log,
        studentName: person?.name || log.studentName || "Member",
        role: person?.role || "student"
      };
    });
  }, [allRecentLogs, allPeople]);

  const filteredLogsForPicking = useMemo(() => {
    return logsWithNames.filter(log => {
      // 1. Filter by Role (Staff vs All)
      // IF you specifically selected a person (like Pankaj), we bypass the role filter 
      // so you can see their logs regardless of the Staff Only toggle.
      if (!logFilterStaffId && logFilterRole === "staff") {
        const isStaff = ["staff", "trainer", "instructor", "admin"].includes(log.role);
        if (!isStaff) return false;
      }

      // 2. Filter by Category
      if (logFilterCategoryId && log.category !== logFilterCategoryId) return false;

      // 3. Optional filters
      const matchesStaff = !logFilterStaffId || log.studentId === logFilterStaffId;
      const matchesDate = !logFilterDate || log.date === logFilterDate;
      const notAlreadySelected = !selectedLogIds.includes(log.entryId);
      
      return matchesStaff && matchesDate && notAlreadySelected;
    });
  }, [logsWithNames, logFilterStaffId, logFilterDate, logFilterRole, logFilterCategoryId, selectedLogIds]);

  const toggleLogSelection = (log) => {
    if (selectedLogIds.includes(log.entryId)) {
      setSelectedLogIds(prev => prev.filter(id => id !== log.entryId));
      setFormData(prev => ({
        ...prev,
        linkedLogs: prev.linkedLogs.filter(l => l.entryId !== log.entryId)
      }));
    } else {
      setSelectedLogIds(prev => [...prev, log.entryId]);
      // Ensure we have the name in the linked log object
      const person = allPeople.find(s => s.id === log.studentId);
      const enrichedLog = {
        ...log,
        studentName: person?.name || log.studentName || "Member"
      };
      setFormData(prev => ({
        ...prev,
        linkedLogs: [...prev.linkedLogs, enrichedLog]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId || formData.items.length === 0) {
      alert("Please select a customer and add at least one item");
      return;
    }

    setLoading(true);
    try {
      await addInvoice({
        ...formData,
        totalAmount: calculateTotal(),
        totalEffortMinutes: totalMinutes,
        valuePerHour,
      });
      navigate("/admin/invoices");
    } catch (err) {
      console.error("Error creating invoice:", err);
      alert("Error creating invoice");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; }
        .modal-card { background: white; border-radius: 16px; width: 500px; padding: 32px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
        .log-modal-card { width: 800px; max-height: 80vh; overflow-y: auto; }
        .form-group { marginBottom: 20px; }
        .form-label { display: block; fontSize: 15px; fontWeight: 600; color: #374151; marginBottom: 8px; }
        .form-input { width: 100%; padding: 12px 16px; border: 1px solid #D1D5DB; borderRadius: 10px; fontSize: 16px; outline: none; box-sizing: border-box; }
        .form-input:focus { border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        .log-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-bottom: 1px solid #eee; cursor: pointer; border-radius: 8px; transition: background 0.2s; }
        .log-item:hover { background: #f9fafb; }
        .log-item.selected { background: #eff6ff; border-color: #3b82f6; }
      `}</style>

      {/* Quick Add Customer Modal */}
      {showCustomerModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 style={{ margin: "0 0 24px 0", fontSize: 22, fontWeight: 700 }}>Quick Add Customer</h3>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" value={newCustomerData.name} onChange={e => setNewCustomerData({...newCustomerData, name: e.target.value})} placeholder="Customer name" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="form-input" value={newCustomerData.phone} onChange={e => setNewCustomerData({...newCustomerData, phone: e.target.value})} placeholder="Mobile number" />
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea className="form-input" value={newCustomerData.address} onChange={e => setNewCustomerData({...newCustomerData, address: e.target.value})} placeholder="City/Town" style={{ height: 80 }} />
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
              <button type="button" onClick={() => setShowCustomerModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #D1D5DB", background: "none", cursor: "pointer", fontSize: 15, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={handleQuickAddCustomer} style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#3B82F6", color: "white", border: "none", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Save Customer</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Item Modal */}
      {showItemModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 style={{ margin: "0 0 24px 0", fontSize: 22, fontWeight: 700 }}>Quick Add Item</h3>
            <div className="form-group">
              <label className="form-label">Item Name *</label>
              <input className="form-input" value={newItemData.name} onChange={e => setNewItemData({...newItemData, name: e.target.value})} placeholder="Product or Service name" />
            </div>
            <div className="form-group">
              <label className="form-label">Default Rate (₹) *</label>
              <input className="form-input" type="number" value={newItemData.rate} onChange={e => setNewItemData({...newItemData, rate: e.target.value})} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-input" value={newItemData.category} onChange={e => setNewItemData({...newItemData, category: e.target.value})}>
                <option value="Service">Service</option>
                <option value="Product">Product</option>
                <option value="Course">Course</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
              <button type="button" onClick={() => setShowItemModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid #D1D5DB", background: "none", cursor: "pointer", fontSize: 15, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={handleQuickAddItem} style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#10B981", color: "white", border: "none", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Save Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Work Log Picker Modal */}
      {showLogModal && (
        <div className="modal-overlay">
          <div className="modal-card log-modal-card">
            <h3 style={{ margin: "0 0 16px 0", fontSize: 20, fontWeight: 700 }}>Link Effort / Work Logs</h3>
            
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, display: "block", marginBottom: 4 }}>Filter by Type</label>
                <select className="form-input" value={logFilterRole} onChange={e => setLogFilterRole(e.target.value)}>
                  <option value="staff">Staff Only (Default)</option>
                  <option value="all">All Members (Students + Staff)</option>
                </select>
              </div>

              <div style={{ flex: "1 1 200px" }}>
                <label style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, display: "block", marginBottom: 4 }}>Filter by Member</label>
                <select className="form-input" value={logFilterStaffId} onChange={e => setLogFilterStaffId(e.target.value)}>
                  <option value="">{logFilterRole === "staff" ? "All Staff" : "All Members"}</option>
                  {allPeople
                    .filter(s => logFilterRole === "all" || ["staff", "trainer", "instructor", "admin"].includes(s.role))
                    .map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>

              <div style={{ flex: "1 1 200px" }}>
                <label style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, display: "block", marginBottom: 4 }}>Category (From Settings)</label>
                <select className="form-input" value={logFilterCategoryId} onChange={e => setLogFilterCategoryId(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
              </div>

              <div style={{ flex: "1 1 150px" }}>
                <label style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, display: "block", marginBottom: 4 }}>Filter by Date</label>
                <input type="date" className="form-input" value={logFilterDate} onChange={e => setLogFilterDate(e.target.value)} />
              </div>

              <div style={{ flex: "1 1 150px", display: "flex", alignItems: "flex-end" }}>
                <button 
                  type="button" 
                  onClick={handleFetchLogs}
                  disabled={fetchingLogs}
                  style={{ 
                    width: "100%", height: 48, borderRadius: 10, border: "none", 
                    background: "#3B82F6", color: "white", fontWeight: 700, cursor: "pointer", 
                    boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.3)", opacity: fetchingLogs ? 0.7 : 1
                  }}
                >
                  {fetchingLogs ? "Fetching..." : "Pull Logs"}
                </button>
              </div>
            </div>

            <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #eee", borderRadius: 12, padding: 8 }}>
              {filteredLogsForPicking.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                   No work/class logs found for the selected filters.
                </div>
              ) : (
                filteredLogsForPicking.map(log => (
                  <div key={log.entryId} className="log-item" onClick={() => toggleLogSelection(log)}>
                    <input type="checkbox" checked={selectedLogIds.includes(log.entryId)} readOnly />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{log.studentName} - {log.topicName} {log.notes && <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 4 }}> ({log.notes})</span>}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(log.date)} | {log.startTime}-{log.endTime} ({minutesToHHMM(HHMMToMinutes(log.endTime) - HHMMToMinutes(log.startTime))}) | Category: {log.category}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
              <button type="button" onClick={() => setShowLogModal(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#111827", color: "white", border: "none", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Done Linking</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Link to="/admin/invoices" style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center", background: "#f3f4f6", padding: 12, borderRadius: "50%" }}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 800, color: "#111827", margin: 0 }}>Create New Invoice</h1>
            <p style={{ margin: "4px 0 0 0", fontSize: 15, color: "#6b7280" }}>Billing Transaction #{invoiceNo}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          
          {/* Section 1: Customer & Staff */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 32 }}>
              <div>
                <label className="form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Bill To (Customer / Student) *</span>
                  <div style={{ display: "flex", gap: 4, background: "#f3f4f6", padding: 3, borderRadius: 8 }}>
                    <button type="button" onClick={() => setCustomerFilter("business")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: customerFilter === "business" ? "white" : "transparent", boxShadow: customerFilter === "business" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", color: customerFilter === "business" ? "#3B82F6" : "#6B7280" }}>Business</button>
                    <button type="button" onClick={() => setCustomerFilter("student")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: customerFilter === "student" ? "white" : "transparent", boxShadow: customerFilter === "student" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", color: customerFilter === "student" ? "#3B82F6" : "#6B7280" }}>Students</button>
                    <button type="button" onClick={() => setCustomerFilter("all")} style={{ padding: "4px 8px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: customerFilter === "all" ? "white" : "transparent", boxShadow: customerFilter === "all" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", color: customerFilter === "all" ? "#3B82F6" : "#6B7280" }}>All</button>
                  </div>
                </label>
                <select value={formData.customerId} onChange={handleCustomerChange} required className="form-input">
                  <option value="">Select Person</option>
                  {(customerFilter === "business" || customerFilter === "all") && (
                    <optgroup label="Business Customers">
                      {customers.map(c => (<option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ""}</option>))}
                    </optgroup>
                  )}
                  {(customerFilter === "student" || customerFilter === "all") && (
                    <optgroup label="Students & Members">
                      {allPeople.map(s => (<option key={s.id} value={s.id}>{s.name} (Roll: {s.roll})</option>))}
                    </optgroup>
                  )}
                </select>
                <button type="button" onClick={() => setShowCustomerModal(true)} style={{ color: "#3B82F6", background: "none", border: "none", padding: "4px 0", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>+ Create New Business Customer</button>
              </div>
              <div>
                <label className="form-label">Invoice Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))} className="form-input" />
              </div>
              <div>
                <label className="form-label">Managed By (Primary Staff)</label>
                <select value={formData.staffId} onChange={handleStaffChange} className="form-input">
                  <option value="">Select Staff Member</option>
                  {staffList.map(s => (<option key={s.id} value={s.id}>{s.name} ({s.role})</option>))}
                </select>
              </div>
              <div>
                <label className="form-label">Status</label>
                <select value={formData.status} onChange={(e) => handleStatusChange(e.target.value)} className="form-input" style={{ background: formData.status === "paid" ? "#f0fdf4" : "#fff7ed", color: formData.status === "paid" ? "#166534" : "#9a3412", fontWeight: 700 }}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              {formData.status === "paid" && (
                <div>
                  <label className="form-label">Deposit To (Payment Method)</label>
                  <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="form-input">
                    <option value="">Select Account</option>
                    {accounts.map(a => (<option key={a.id} value={a.name}>{a.name}</option>))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Items & Services */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Items & Services</h3>
              <button type="button" onClick={() => setShowItemModal(true)} style={{ color: "#10B981", background: "#ECFDF5", border: "none", padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Create New Item</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 100px 140px 140px auto", gap: 16, marginBottom: 32, background: "#f9fafb", padding: 24, borderRadius: 16 }}>
              <div>
                <label className="form-label">Select Item</label>
                <select value={newItem.itemId} onChange={handleItemChange} className="form-input">
                  <option value="">Choose item...</option>
                  {items.map(item => (
                    <option key={item.id} value={item.id}>{item.name} - ₹{item.rate}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Qty</label>
                <input type="number" min="0" step="0.01" value={newItem.quantity} onChange={(e) => handleQuantityChange(e.target.value)} className="form-input" />
              </div>
              <div>
                <label className="form-label">Rate (₹)</label>
                <input type="number" min="0" step="0.01" value={newItem.rate} onChange={(e) => handleRateChange(e.target.value)} className="form-input" />
              </div>
              <div>
                <label className="form-label">Total</label>
                <div style={{ padding: "12px", background: "#e5e7eb", borderRadius: 10, fontSize: 16, fontWeight: 800, textAlign: "right" }}>₹{(newItem.quantity * newItem.rate).toFixed(2)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button type="button" onClick={handleAddItem} style={{ height: 48, padding: "0 24px", background: "#111827", color: "white", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 15 }}>Add Item</button>
              </div>
              <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                 <input type="text" value={newItem.description} onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))} placeholder="Optional notes for this item (e.g. batch name, period, serial no.)" className="form-input" style={{ fontSize: 14 }} />
              </div>
            </div>

            {formData.items.length > 0 && (
              <div style={{ borderTop: "2px solid #f3f4f6", paddingTop: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Item Details</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Qty x Rate</th>
                      <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Amount</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontSize: 13, fontWeight: 700, color: "#6B7280", textTransform: "uppercase" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "16px" }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{item.itemName}</div>
                          {item.description && <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>{item.description}</div>}
                        </td>
                        <td style={{ padding: "16px", textAlign: "center" }}>
                          <div style={{ fontSize: 15, color: "#374151" }}>{item.quantity} × {formatCurrency(item.rate)}</div>
                        </td>
                        <td style={{ padding: "16px", textAlign: "right" }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>{formatCurrency(item.total)}</div>
                        </td>
                        <td style={{ padding: "16px", textAlign: "center" }}>
                          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                            <button type="button" onClick={() => handleEditItem(idx)} style={{ color: "#3b82f6", background: "#EBF1FF", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Edit</button>
                            <button type="button" onClick={() => handleRemoveItem(idx)} style={{ color: "#ef4444", background: "#FEF2F2", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section 3: Effort Tracking / Work Logs */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Effort Tracking (Linked Logs)</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: 14, color: "#6B7280" }}>Select work logs to calculate task value and track staff efforts.</p>
              </div>
              <button type="button" onClick={() => setShowLogModal(true)} style={{ background: "#3B82F6", color: "white", border: "none", padding: "10px 20px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>+ Link Logs</button>
            </div>

            {formData.linkedLogs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px", color: "#9ca3af", background: "#f9fafb", borderRadius: 16, border: "1px dashed #e5e7eb" }}>
                No work logs linked yet. Click "Link Logs" to select staff entries.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
                {formData.linkedLogs.map(log => (
                  <div key={log.entryId} style={{ padding: 16, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, position: "relative" }}>
                    <button type="button" onClick={() => toggleLogSelection(log)} style={{ position: "absolute", top: 12, right: 12, border: "none", background: "none", color: "#ef4444", cursor: "pointer", fontSize: 18 }}>×</button>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{log.studentName}</div>
                    <div style={{ fontSize: 13, color: "#374151", margin: "4px 0" }}>{log.topicName} {log.notes && <span style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>- {log.notes}</span>}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(log.date)} | {log.startTime}-{log.endTime} ({minutesToHHMM(HHMMToMinutes(log.endTime)-HHMMToMinutes(log.startTime))})</div>
                  </div>
                ))}
              </div>
            )}

            {formData.linkedLogs.length > 0 && (
              <div style={{ marginTop: 24, padding: 20, background: "#f0fdf4", borderRadius: 12, display: "flex", gap: 40 }}>
                 <div>
                    <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>Total Effort</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#166534" }}>{minutesToHHMM(totalMinutes)} <span style={{ fontSize: 14, fontWeight: 500 }}>({(totalMinutes/60).toFixed(2)} hrs)</span></div>
                 </div>
                 <div>
                    <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>Task Value per Hour</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#166534" }}>{formatCurrency(valuePerHour)}/hr</div>
                 </div>
              </div>
            )}
          </div>

          {/* Section 4: Final Summary */}
          <div style={{ background: "#111827", borderRadius: 24, padding: 40, color: "white", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
              <div>
                <h3 style={{ margin: "0 0 24px 0", fontSize: 16, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px" }}>Payment Summary</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
                    <span style={{ color: "#9ca3af" }}>Items Subtotal</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                  {totalMinutes > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
                      <span style={{ color: "#9ca3af" }}>Total Effort (Hrs)</span>
                      <span style={{ fontWeight: 600 }}>{(totalMinutes/60).toFixed(2)} hrs</span>
                    </div>
                  )}
                  <div style={{ height: 1, background: "#374151", margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>Grand Total</span>
                    <span style={{ fontSize: 36, fontWeight: 900, color: "#3B82F6" }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>

              <div style={{ background: "#1f2937", padding: 32, borderRadius: 20 }}>
                {formData.status === "paid" && (
                  <div style={{ marginBottom: 24 }}>
                    <label className="form-label" style={{ color: "#9ca3af" }}>Deposit Funds To</label>
                    <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="form-input" style={{ background: "#111827", border: "1px solid #374151", color: "white", height: 50 }}>
                      <option value="">Select Account</option>
                      {accounts.map(a => (<option key={a.id} value={a.name}>{a.name}</option>))}
                    </select>
                  </div>
                )}

                <button type="submit" disabled={loading || formData.items.length === 0} style={{ width: "100%", padding: "18px", background: "#3B82F6", color: "white", border: "none", borderRadius: 12, fontSize: 18, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)" }}>
                  {loading ? "Processing..." : "Finalize & Save Invoice"}
                </button>
                
                <Link to="/admin/invoices" style={{ display: "block", textAlign: "center", marginTop: 20, fontSize: 15, color: "#9ca3af", textDecoration: "none", fontWeight: 600 }}>Discard & Go Back</Link>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
